import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Switch } from '@mui/material';

import InputLabel from 'shared/components/forms/InputLabel/InputLabel';
import BasicDialog from 'shared/components/feedback/BasicDialog';
import { useConsult } from 'shared/context/ConsultContext';
import ActionButton from 'shared/components/buttons/ActionButton';
import { useDispatch } from 'react-redux';
import { appStateActions } from 'store/appStateSlice';
import { SnackbarTypeEnum } from 'shared/components/feedback/Snackbar/SnackbarTypes.enum';
import { consultsActions } from 'features/consults/store/slice';
import { CONSULT_TABS } from 'features/consults/utils/constants';

const LockChart = ({
  label = null,
  size = 'medium',
  color = 'primary',
  dialogTitle,
  disabled,
  checked,
  onChange,
}) => {
  const dispatch = useDispatch();
  const { consult } = useConsult();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [choiceDialogVisible, setChoiceDialogVisible] = useState(false);

  const requiresChoiceDialog = () =>
    (consult.type === 'Eeg' || consult.type === 'EegPediatric') &&
    consult.demographics.observationType === 'ObservationDay' &&
    !consult.demographics.studyId;

  const handleLockAttempt = () => {
    if (requiresChoiceDialog()) {
      setChoiceDialogVisible(true);
    } else {
      setDialogVisible(true);
    }
  };

  const handleOption1 = () => {
    dispatch(
      consultsActions.editConsult({
        consultId: consult.id,
        tab: CONSULT_TABS.DEMOGRAPHICS,
        changes: {
          observationType: 'InitialRead',
        },
        cb: () =>
          dispatch(
            consultsActions.getConsultById({
              consultId: consult.id,
              forceReload: false,
            }),
          ).then(() => {
            onChange();
          }),
      }),
    );
    setChoiceDialogVisible(false);
  };

  const handleOption2 = () => {
    const hasICD = consult.signature.icdCodes?.length > 0;
    const hasCPT = consult.signature.eegcptCodes?.length > 0;

    if (!hasICD || !hasCPT) {
      dispatch(
        appStateActions.showSnackbar({
          text: 'Please fill in ICD-10 and CPT code fields before locking.',
          type: SnackbarTypeEnum.WARNING,
          duration: 5000,
        }),
      );
      return;
    }

    setChoiceDialogVisible(false);
    setDialogVisible(true);
  };

  return (
    <>
      {label && <InputLabel>{label}</InputLabel>}
      <Switch
        disabled={disabled}
        size={size}
        color={color}
        checked={checked}
        onChange={handleLockAttempt}
      />
      <BasicDialog
        title={dialogTitle}
        open={dialogVisible}
        handleConfirm={() => {
          setDialogVisible(false);
          onChange();
        }}
        handleCancel={() => setDialogVisible(false)}
      />
      <BasicDialog
        title="Choose the correct option for this consult"
        open={choiceDialogVisible}
        handleConfirm={() => {}}
        content={
          <>
            <ActionButton fullWidth variant="outlined" onClick={handleOption1}>
              This is the first billable read for an ongoing study
            </ActionButton>
            <ActionButton fullWidth variant="outlined" onClick={handleOption2}>
              This is a one and done read
            </ActionButton>
          </>
        }
        handleCancel={() => setChoiceDialogVisible(false)}
        confirmText=""
        cancelText="Cancel"
      />
    </>
  );
};

LockChart.propTypes = {
  label: PropTypes.string,
  size: PropTypes.string,
  color: PropTypes.string,
  dialogTitle: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default LockChart;
